<template>
  <div class="min-h-screen bg-gradient-to-br from-emerald-50 to-green-50">
    <!-- Header Component -->
    <SiteHeader 
      :currentSite="'bittermelon'" 
      :activeSection="activeSection"
      @toggleMobileMenu="handleToggleMobileMenu"
      @openContactModal="handleOpenContactModal"
      @openLoginModal="handleOpenLoginModal"
      @smoothScrollTo="handleSmoothScrollTo"
    />

    <!-- Header Banner -->
    <section id="home" class="relative overflow-hidden">
      <div class="relative bg-gradient-to-r from-emerald-500 to-green-600 h-96">
        <div class="absolute inset-0 bg-black bg-opacity-30"></div>
        <img src="https://vue-project.sgp1.digitaloceanspaces.com/2025/10/1761117378560.jpeg" alt="คลัสเตอร์มะระ" class="w-full h-96 object-cover">
        <div class="absolute inset-0 flex items-center justify-center">
          <div class="text-center text-white px-4">
            <h2 class="text-5xl font-bold mb-4">คลัสเตอร์มะระ</h2>
            <p class="text-xl mb-8 max-w-3xl mx-auto">มะระไทยเพื่อสุขภาพโลก ผลิตภัณฑ์จากมะระที่มีคุณประโยชน์ต่อสุขภาพ</p>
            <button class="bg-white text-emerald-600 px-8 py-3 rounded-lg font-semibold hover:bg-emerald-50 transition-colors shadow-lg">
              เรียนรู้เพิ่มเติม
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Main Content -->
    <main class="relative">
      <!-- Dashboard Report Section -->
      <section id="dashboard">
        <ClusterPerformanceSection
          :clusterId="activeClusterId"
          :cluster="cluster"
          @cluster-data-loaded="handleClusterDataLoaded"
        />
      </section>

      <!-- Products Section -->
      <section id="products">
        <ProductsSection :clusterId="activeClusterId" />
      </section>

      <!-- Articles Section -->
      <section id="articles">
        <ArticlesSection :clusterId="activeClusterId" />
      </section>

      <!-- Gallery Section -->
      <section id="gallery">
        <GallerySection :clusterId="activeClusterId" />
      </section>
    </main>
    
    <!-- Footer Component -->
    <SiteFooter />
  </div>
</template>

<script setup>
import SiteHeader from '~/layouts/theme/custom/clustersie/components/SiteHeader.vue'
import SiteFooter from '~/layouts/theme/custom/clustersie/components/SiteFooter.vue'
import ClusterPerformanceSection from '~/layouts/theme/custom/clustersie/sites/section/ClusterPerformanceSection.vue'
import ProductsSection from '~/layouts/theme/custom/clustersie/sites/section/ProductsSection.vue'
import ArticlesSection from '~/layouts/theme/custom/clustersie/sites/section/ArticlesSection.vue'
import GallerySection from '~/layouts/theme/custom/clustersie/sites/section/GallerySection.vue'
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useNuxtApp } from '#app'
import { useRoute } from '#app'

// State management
const activeSection = ref('home')
const { $DSS } = useNuxtApp()
const route = useRoute()

// Cluster management - ดึงข้อมูลจาก API ทั้งหมด
const cluster = ref(null)
const activeClusterId = ref(null)

// Hardcode cluster ID สำหรับ fallback (สำหรับแต่ละ site)
const FALLBACK_CLUSTER_ID = '680b85cd1cea2ce6a63fad25' // Bitter Melon cluster ID

// Check if clusterData exists in localStorage
const checkLocalStorageClusterData = () => {
  if (typeof window !== 'undefined') {
    try {
      const storedClusterData = localStorage.getItem('clusterData')
      if (storedClusterData) {
        const parsedCluster = JSON.parse(storedClusterData)
        return parsedCluster
      }
    } catch (e) {
      console.error('Error parsing clusterData from localStorage:', e)
    }
  }
  return null
}

// Fetch cluster by hardcode ID และเก็บใน localStorage
const fetchAndStoreClusterData = async (clusterId) => {
  try {
    console.log('Fetching cluster by ID for localStorage storage:', clusterId)
    const response = await $DSS(`cluster/${clusterId}`)
    if (response && response.success) {
      // เก็บข้อมูลใน localStorage
      localStorage.setItem('clusterData', JSON.stringify(response.data))
      activeClusterId.value = response.data._id || response.data.id
      cluster.value = response.data
      console.log('Cluster data stored in localStorage:', response.data)
      return response.data
    } else {
      console.error('Failed to fetch cluster by hardcode ID:', response)
      return null
    }
  } catch (error) {
    console.error('Error fetching cluster by hardcode ID:', error)
    return null
  }
}

// Fetch available clusters (fallback ในกรณีที่หาไม่เจอ)
const fetchAvailableClusters = async () => {
  try {
    console.log('Fetching available clusters as last resort...')
    const response = await $DSS('clusters')
    if (response && response.success && response.data && response.data.length > 0) {
      // เอา cluster แรกที่เจอ
      const firstCluster = response.data[0]
      activeClusterId.value = firstCluster._id || firstCluster.id
      cluster.value = firstCluster
      console.log('Using first available cluster:', firstCluster)
      return firstCluster
    }
  } catch (error) {
    console.error('Error fetching available clusters:', error)
  }
  return null
}

// Unified function to fetch cluster data with fallback hierarchy
const fetchCluster = async () => {
  try {
    // 1. ลองเช็ค localStorage ก่อน
    console.log('Checking localStorage for clusterData...')
    const localCluster = checkLocalStorageClusterData()
    if (localCluster) {
      activeClusterId.value = localCluster._id || localCluster.id
      cluster.value = localCluster
      console.log('Using cluster from localStorage:', localCluster)
      return
    }
    
    // 2. ถ้าไม่มีใน localStorage ให้ใช้ hardcode ID
    console.log('No localStorage data, fetching by hardcode ID:', FALLBACK_CLUSTER_ID)
    const fetchedCluster = await fetchAndStoreClusterData(FALLBACK_CLUSTER_ID)
    if (fetchedCluster) {
      return
    }
    
    // 3. ถ้าทุกอย่างล้มเหลว ให้ลองดึง clusters ทั้งหมด
    console.log('Fallback: trying to fetch available clusters...')
    await fetchAvailableClusters()
    
  } catch (error) {
    console.error('Error fetching cluster:', error)
    // ถ้า error ให้ลองหา cluster ที่ใช้ได้
    await fetchAvailableClusters()
  }
}

// Cluster performance data (managed by ClusterPerformanceSection)
const clusterPerformanceData = ref(null)

// Handle cluster performance data from ClusterPerformanceSection
const handleClusterDataLoaded = (data) => {
  clusterPerformanceData.value = data
  console.log('Cluster performance data loaded:', data)
}

// Currency formatting (ใช้สำหรับ sections อื่น)
const formatCurrency = (amount) => {
  try {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0)
  } catch {
    return '฿0'
  }
}

// Handle events from header component
const handleToggleMobileMenu = () => {
  console.log('Toggle mobile menu')
}

const handleOpenContactModal = () => {
  // Contact modal removed
}

const handleOpenLoginModal = () => {
  console.log('Open login modal')
}

const handleSmoothScrollTo = (sectionId) => {
  const element = document.getElementById(sectionId)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
    activeSection.value = sectionId
  }
}

// Smooth scroll function
const smoothScrollTo = (targetId, event) => {
  event?.preventDefault()
  
  // Handle home section - scroll to top
  if (targetId === 'home') {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    return
  }
  
  // Handle other sections
  const target = document.getElementById(targetId)
  if (!target) {
    return
  }
  
  // Calculate offset for sticky header
  const headerHeight = 80
  const targetPosition = target.offsetTop - headerHeight
  
  // Use native smooth scroll for immediate response
  window.scrollTo({ 
    top: targetPosition, 
    behavior: 'smooth' 
  })
}

// No slider functionality needed

// Scroll detection for active section
const handleScroll = () => {
  const currentScrollY = window.scrollY
  const sections = ['home', 'dashboard', 'products', 'articles', 'gallery']
  const scrollPos = currentScrollY + 100

  // Check if we're at the top of the page (home section)
  if (currentScrollY < 50) {
    activeSection.value = 'home'
    return
  }

  // Find active section based on scroll position
  for (let i = sections.length - 1; i >= 0; i--) {
    if (sections[i] === 'home') continue
    
    const section = document.getElementById(sections[i])
    if (section && section.offsetTop <= scrollPos) {
      activeSection.value = sections[i]
      break
    }
  }
}

// Lifecycle hooks
onMounted(async () => {
  try {
    // Initialize cluster data
    console.log('Initializing BitterMelonSite...')
    
    // ใช้ fetchCluster ที่จัดการ fallback ได้ครบถ้วน
    // จะเช็ค localStorage -> URL params -> FALLBACK_CLUSTER_ID -> API clusters ตามลำดับ
    await fetchCluster()
    
    // Setup scroll listeners
    window.addEventListener('scroll', handleScroll)
    handleScroll() // Initial check
    
  } catch (error) {
    console.error('Error initializing BitterMelonSite:', error)
  }
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
/* Bitter Melon site specific styles */
.bg-gradient-to-br {
  background: linear-gradient(to bottom right, #ecfdf5, #f0fdf4);
}

/* Banner Styles */
.banner {
  position: relative;
  height: 24rem; /* 96 in Tailwind */
}

/* Smooth scroll for one-page navigation */
html {
  scroll-behavior: smooth;
}

/* Adjust scroll position for fixed header */
section {
  scroll-margin-top: 80px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .banner h2 {
    font-size: 2.5rem;
  }
  
  .banner p {
    font-size: 1rem;
  }
}

@media (max-width: 640px) {
  .banner h2 {
    font-size: 2rem;
  }
  
  .banner p {
    font-size: 0.875rem;
  }
}
</style>